import { ToggleVisibilityButton } from "components/buttons/linkButtons/toggleVisibilityButton/ToggleVisibilityButton";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { sum } from "lodash";
import React from "react";
import {
  ContentItemMenu,
  DeleteLinkButton,
  DeletionRestrictedTooltip,
  DuplicateLinkButton,
  EditLinkButton,
  EventCard,
  ViewContentLinkButton,
} from "../../../../../components";
import { ContentTypesEnum, RolePermissions, Strings } from "../../../../../enums";
import { type CardBase } from "../../../../../interfaces";
import { type EventOverview } from "../../types/state";
import { getContentLinkToEupFromPreview } from "utils/eupUtils";

export interface OverviewCardProps extends CardBase<EventOverview> {
  onCopy: (id: number[]) => () => void;
  onDelete: (id: number[], flowsCount?: number, packsCount?: number) => () => void;
  onEdit: (id: number, isDraft?: boolean) => () => void;
  readonly?: boolean;
  onToggleVisibility: (id: number[], hidden: boolean) => () => void;
}

export const OverviewCard: React.FC<OverviewCardProps> = (props: OverviewCardProps): React.ReactElement => {
  const { item, readonly, selected, onEdit, onCopy, onDelete, onSelect, disabled, onToggleVisibility } = props;
  const { id, isPurchased, canBeDeleted, isDraft } = item;
  const deleteContentEnabled = useFeatureFlag(FeatureFlags.DeleteContentWithDependenciesFeature);

  const entityType = "event";
  const { flowsCount = 0, packsCount = 0, assignmentsCount = 0 } = item.dependencies || {};
  const dependencies = { flows: flowsCount, packs: packsCount, "users or groups": assignmentsCount };

  const renderPopupMenu = () => (
    <ContentItemMenu
      circle
      outlinedEllipsis
      isChecked={selected}
      item={{
        isPurchased,
        canBeDeleted,
      }}
      readonly={readonly}
      entityType={entityType}
      dependencies={dependencies}
      purchasedTooltipContent={Strings.tooltipMessages.purchased.oneDeleteDisabled(entityType)}
    >
      <ViewContentLinkButton
        href={getContentLinkToEupFromPreview[ContentTypesEnum.Events](id)}
        disabled={!item.hasBeenPublished}
      />
      {item.hasBeenPublished && (
        <ToggleVisibilityButton
          onClick={onToggleVisibility([item.id], !item.visibility)}
          hidden={!item.visibility}
          requiredPermissions={[RolePermissions.EventsCreate]}
        />
      )}
      {!isPurchased && <EditLinkButton onClick={onEdit?.(id, isDraft)} />}
      {!isPurchased && <DuplicateLinkButton onClick={onCopy?.([id])} />}
      {!isPurchased && (
        <DeleteLinkButton
          isDisabled={deleteContentEnabled ? isPurchased : !canBeDeleted}
          after={
            !deleteContentEnabled &&
            !!sum(Object.values(dependencies)) && (
              <DeletionRestrictedTooltip dependencies={dependencies} entityType={entityType} />
            )
          }
          onClick={onDelete?.([id], flowsCount, packsCount)}
        />
      )}
    </ContentItemMenu>
  );

  return (
    <EventCard
      item={item}
      disabled={disabled}
      url={props.item.id.toString()}
      onSelect={onSelect}
      popupMenu={renderPopupMenu()}
      selected={selected}
    />
  );
};
